import React, { useEffect, useRef, useState } from 'react';
import axiosClient from '@/api/axiosClient';
import API from '@/api/endpoints';
import { useT } from '@/i18n/labels';
import { useConversationStore } from '@/stores/conversationStore';
import { getConversationLabel } from '@/utils/conversationLabel';
import type { Conversation } from '@/types';

interface ConversationSubjectProps {
  conv: Conversation;
  /**
   * The conversation is not mine to edit (a teammate owns it, or its owner is not yet verified): the name is shown
   * but cannot be opened for editing. UX only, not security. The server does NOT check ownership until PR-A2
   * (customer send, close, subject, category); until then this gate is the only thing stopping it.
   */
  disabled?: boolean;
}

/**
 * The conversation's NAME, editable in place.
 *
 * <b>Stored vs derived.</b> `conv.subject` is what a human named this conversation; when it is null
 * — the normal case — we show {@link getConversationLabel}'s per-channel derivation instead (email
 * subject, sender name, "Anonymous - {first message}", …). That helper is REUSED, never
 * reimplemented: it already encodes the never-show-a-raw-GUID rules.
 *
 * <b>The editor seeds from `subject`, not from the derived label.</b> If it seeded from the derived
 * text, opening the editor and pressing Save without typing would FREEZE a message preview into
 * storage as a permanent name — and that preview moves, so whatever happened to be the last message
 * at that moment would become the title forever. The derived label is the placeholder instead, which
 * shows the same text without committing to it.
 *
 * Clearing the field stores null, which returns the conversation to its derived label. That is the
 * only way to undo a rename, so the empty submit is a real, supported action rather than a no-op.
 */
export const ConversationSubject: React.FC<ConversationSubjectProps> = ({ conv, disabled = false }) => {
  const { t } = useT();
  const setConversationSubject = useConversationStore((s) => s.setConversationSubject);

  const derived = getConversationLabel(conv);
  const stored = (conv.subject ?? '').trim();
  const shown = stored || derived;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(stored);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const open = () => {
    setDraft(stored);
    setError(null);
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setError(null);
  };

  const save = async () => {
    const next = draft.trim();
    const previous = conv.subject ?? null;
    const value = next.length > 0 ? next : null;

    // Nothing changed — close without a request. The server treats this as a no-op too, but not
    // asking at all is faster and avoids a pointless round-trip on an accidental open-and-close.
    if (value === previous) {
      setEditing(false);
      return;
    }

    setSaving(true);
    setError(null);
    // Optimistic, with the previous value kept so a rejection can restore it — the same shape as
    // CrmCategoryPicker's save. Waiting for the round trip makes an inline editor feel broken.
    setConversationSubject(conv.sessionId, value);

    try {
      await axiosClient.post(API.SESSIONS.SET_SUBJECT(conv.sessionId), {
        subject: value,
        origin: 'console',
      });
      setEditing(false);
    } catch {
      // Put the old name back and SAY so. A silent revert looks like the rename simply did not
      // register, and the agent retypes it forever.
      setConversationSubject(conv.sessionId, previous);
      setError(t('conv_subject_save_failed'));
    } finally {
      setSaving(false);
    }
  };

  // Disabled wins over an editor that was already open: the owner changed under the agent mid-edit, so the editor
  // closes to the read-only name rather than offering a Save that can only be refused.
  if (!editing || disabled) {
    return (
      <div className="ac-ctx-sec ac-ctx-subject" data-testid="conv-subject">
        <div className="ac-ctx-sec-title">{t('conv_subject')}</div>
        <button
          type="button"
          className="ac-ctx-subject-btn"
          onClick={open}
          disabled={disabled}
          title={disabled ? undefined : t('conv_subject_edit')}
          aria-label={t('conv_subject_edit')}
          data-testid="conv-subject-edit"
        >
          {shown}
        </button>
        {error && (
          <div className="ac-ctx-na ac-ctx-error" data-testid="conv-subject-error">
            {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="ac-ctx-sec ac-ctx-subject" data-testid="conv-subject">
      <div className="ac-ctx-sec-title">{t('conv_subject')}</div>
      <input
        ref={inputRef}
        type="text"
        value={draft}
        maxLength={200}
        disabled={saving}
        placeholder={derived || t('conv_subject_placeholder')}
        aria-label={t('conv_subject')}
        data-testid="conv-subject-input"
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void save();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
          }
        }}
      />
      <div className="ac-ctx-subject-actions">
        <button
          type="button"
          className="ac-btn sm"
          onClick={() => void save()}
          disabled={saving}
          data-testid="conv-subject-save"
        >
          {t('conv_subject_save')}
        </button>
        <button
          type="button"
          className="ac-btn ghost sm"
          onClick={cancel}
          disabled={saving}
          data-testid="conv-subject-cancel"
        >
          {t('conv_subject_cancel')}
        </button>
      </div>
      {error && (
        <div className="ac-ctx-na ac-ctx-error" data-testid="conv-subject-error">
          {error}
        </div>
      )}
    </div>
  );
};
